import { useCallback, useEffect, useMemo, useState } from 'react';
import api, { errMsg } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ReferralSourcesDialog from '../components/ludo/ReferralSourcesDialog';
import { toast } from 'sonner';
import { AlertTriangle, ListPlus, Plus, Search } from 'lucide-react';
import LudoClientDrawer from '../components/ludo/LudoClientDrawer';
import { ORG_META } from '../lib/org';

export default function LudoPipeline() {
  const [schema, setSchema] = useState(null);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payers, setPayers] = useState([]);
  const [stage, setStage] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [editSources, setEditSources] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get('/ludo/clients'), api.get('/ludo/summary')]);
      setClients(c.data.clients || []); setSummary(s.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    api.get('/ludo/schema').then((r) => setSchema(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get('/vault/payers').then((r) => setPayers((r.data.payers || []).map((p) => p.payer_name || p.name).filter(Boolean))).catch(() => {});
    load();
  }, [load]);

  const rows = useMemo(() => clients.filter((c) => (stage === 'all' || c.stage === stage)
    && `${c.client_name} ${c.guardian?.name || ''} ${c.primary_ins?.payer || ''}`.toLowerCase().includes(q.toLowerCase())), [clients, stage, q]);

  if (!schema) return <div className="p-8 text-[#685B72]">Loading…</div>;
  const chips = ['all', ...schema.stages, ...schema.side_stages];

  return (
    <div className="space-y-6" data-testid="ludo-pipeline-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <img src={ORG_META.ludo.logo} alt="Ludo ABA" className="w-14 h-14 rounded-full" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#87A096] font-semibold">Ludo ABA · Tomball</div>
            <h1 className="font-serif text-4xl text-[#2D2833]">Client Pipeline</h1>
            <p className="text-[#685B72] mt-1 text-sm">Intake → insurance check → assessment → authorization → start of care. Each stage requires its paperwork before a client can move forward.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditSources(true)} className="rounded-full" data-testid="ludo-edit-referrals-btn"><ListPlus size={15} className="mr-1" />Referral sources</Button>
          <Button onClick={() => setOpenId('new')} className="rounded-full bg-[#5E5068] hover:bg-[#4A3F53] text-white" data-testid="ludo-add-client-btn"><Plus size={15} className="mr-1" />New client</Button>
        </div>
      </div>

      {summary?.alerts?.length > 0 && (
        <Card className="rounded-2xl border-[#E8C9C9] bg-[#FBEDED] p-4" data-testid="ludo-alerts">
          {summary.alerts.map((a) => (
            <button key={a.client_id + a.alert} onClick={() => setOpenId(a.client_id)} className="flex items-center gap-2 text-[13px] text-[#8A3A3A] hover:underline">
              <AlertTriangle size={14} /><b>{a.client_name}</b> — {a.alert}
            </button>
          ))}
        </Card>
      )}

      <div className="flex gap-2 flex-wrap" data-testid="ludo-stage-chips">
        {chips.map((s) => (
          <button key={s} onClick={() => setStage(s)} data-testid={`ludo-chip-${s.replace(/\s+/g, '-').toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${stage === s ? 'bg-[#5E5068] text-white border-[#5E5068]' : 'bg-white text-[#5E5068] border-[#E5E0EA] hover:bg-[#F4F2F7]'}`}>
            {s === 'all' ? `All · ${clients.length}` : `${s} · ${summary?.by_stage?.[s] ?? 0}`}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-2.5 text-[#8A7B95]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search child, guardian, payer" className="pl-8 rounded-full h-9 w-64" data-testid="ludo-search" />
        </div>
      </div>

      <Card className="rounded-2xl border-[#E5E0EA] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F7F5FA] text-[#6E5F78] text-left text-[12px]">
            <th className="px-4 py-3">Child</th><th className="px-4 py-3">Guardian</th><th className="px-4 py-3">Payer</th>
            <th className="px-4 py-3">Benefits</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Next step needs</th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.client_id} onClick={() => setOpenId(c.client_id)} className="border-t border-[#EEEAF2] hover:bg-[#FAF9FC] cursor-pointer" data-testid={`ludo-row-${c.client_id}`}>
                <td className="px-4 py-3 font-medium text-[#2D2833]">{c.client_name || '—'}{c.alerts?.length > 0 && <AlertTriangle size={13} className="inline ml-1.5 text-[#8A3A3A]" />}</td>
                <td className="px-4 py-3 text-[#685B72]">{c.guardian?.name || '—'}<div className="text-[11px]">{c.guardian?.phone}</div></td>
                <td className="px-4 py-3 text-[#685B72]">{c.primary_ins?.payer || '—'}</td>
                <td className="px-4 py-3 text-[12px]">{c.vob?.status || 'Not started'}</td>
                <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full bg-[#EFEAF5] text-[#5E5068] text-[12px] font-semibold">{c.stage}</span></td>
                <td className="px-4 py-3 text-[12px] text-[#8A7B95]">{c.next_stage ? (c.missing_for_next.length ? `${c.missing_for_next.length} item(s) for ${c.next_stage}` : `Ready for ${c.next_stage}`) : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#8A7B95]" data-testid="ludo-empty">No clients here yet. Convert an online inquiry or add one with New client.</td></tr>}
          </tbody>
        </table>
      </Card>

      <ReferralSourcesDialog open={editSources} sources={schema.referral_sources} onClose={() => setEditSources(false)}
        onSaved={(src) => { setSchema({ ...schema, referral_sources: src }); setEditSources(false); }} />
      <LudoClientDrawer clientId={openId} schema={schema} payers={payers} onClose={() => setOpenId(null)} onChanged={load}
        onCreated={(id) => { load(); setOpenId(id); }} />
    </div>
  );
}
